import type { ReportCatalogItem, ReportKey } from "@station/contracts";
import { type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DateRangePicker,
  Label,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarClock, Settings2, Star } from "lucide-react";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useFavoriteToggle,
  useReportCatalog,
  useReportPermissions,
} from "../../../features/reports/queries.js";
import { rangeError, rangeLimitFor, resolveRangePreset } from "../../../features/reports/report-values.js";
import { ExportButtons, ReportsGate, useScopeOptions } from "../../../features/reports/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { useDocumentTitle } from "../../../lib/hooks.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

export const Route = createFileRoute("/_app/raporlar/")({ component: ReportCenterRoute });

/** Raporun ekranı (günlük satış özeti yalnızca dışa aktarılır — satırları istasyon × gün × birim tablosudur). */
const REPORT_PAGES: Partial<Record<ReportKey, string>> = {
  STATION_COMPARISON: "/raporlar/karsilastirma",
  FUEL_ANALYTICS: "/raporlar/akaryakit",
  PROFITABILITY: "/raporlar/karlilik",
  FINANCE_OVERVIEW: "/raporlar/finans",
  MAINTENANCE: "/bakim/raporlar",
};

/**
 * Rapor merkezi (Prompt 22, ADR 0022 §12–14): kullanıcının görebildiği raporlar (favoriler önce), her rapor için ekran
 * bağlantısı ve seçilen dönemle Excel / PDF dışa aktarımı; zamanlanmış raporlar ve rapor ayarlarına geçiş.
 */
function ReportCenterRoute(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("menu.reportsCenter"));
  return (
    <>
      <PageHeader title={t("menu.reportsCenter")} description={t("reports.center.description")} />
      <ReportsGate>
        <ReportCenter />
      </ReportsGate>
    </>
  );
}

function ReportCenter(): ReactElement {
  const { t } = useTranslation();
  const permissions = useReportPermissions();
  const catalog = useReportCatalog();
  const options = useScopeOptions();
  const shape = useTenantShape();
  const today = toIstanbulDate(new Date());
  const [range, setRange] = useState(() => resolveRangePreset("LAST_30_DAYS", today));
  const [companyId, setCompanyId] = useState<string | null>(null);
  const companyFieldId = useId();
  const effectiveCompany = companyId ?? (options.companies.length === 1 ? options.companies[0]!.id : null);

  const items = [...(catalog.data?.items ?? [])].sort(
    (left, right) =>
      Number(right.favorite) - Number(left.favorite) || Number(right.available) - Number(left.available),
  );

  return (
    <div className="grid gap-6">
      <Card
        className="flex flex-wrap items-end gap-4 p-4"
        role="search"
        aria-label={t("reports.center.exportScope")}
      >
        <DateRangePicker
          label={t("reports.filter.range")}
          value={range}
          onValueChange={(next) => {
            if (next.from && next.to) setRange({ from: next.from as LocalDate, to: next.to as LocalDate });
          }}
        />
        {options.companies.length > 1 ? (
          <div className="grid gap-1.5">
            <Label htmlFor={companyFieldId}>{t("reports.filter.company")}</Label>
            <Select
              id={companyFieldId}
              className="w-56"
              value={companyId ?? ""}
              onChange={(event) => setCompanyId(event.target.value || null)}
            >
              <option value="">{t("reports.filter.allCompanies")}</option>
              {options.companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.shortName ?? company.legalName}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        <p className="max-w-md text-xs text-muted-foreground">{t("reports.center.exportHint")}</p>
        <div className="ml-auto flex flex-wrap gap-2">
          {permissions.canSchedule ? (
            <Button variant="outline" asChild>
              <Link to="/raporlar/zamanlanmis">
                <CalendarClock aria-hidden="true" />
                {t("menu.reportsSchedules")}
              </Link>
            </Button>
          ) : null}
          {permissions.canManageSettings ? (
            <Button variant="outline" asChild>
              <Link to="/raporlar/ayarlar">
                <Settings2 aria-hidden="true" />
                {t("menu.reportsSettings")}
              </Link>
            </Button>
          ) : null}
        </div>
      </Card>

      {catalog.error ? <Alert tone="danger">{errorMessage(catalog.error)}</Alert> : null}
      {catalog.isPending ? (
        <SkeletonRows rows={5} />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2" aria-label={t("reports.center.listLabel")}>
          {items.map((item) => (
            <li key={item.key}>
              <ReportCard
                item={item}
                range={range}
                companyId={effectiveCompany}
                page={
                  item.key === "STATION_COMPARISON" && shape.isSingleStation
                    ? undefined
                    : REPORT_PAGES[item.key]
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReportCard({
  item,
  range,
  companyId,
  page,
}: {
  item: ReportCatalogItem;
  range: { from: LocalDate; to: LocalDate };
  companyId: string | null;
  page: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const favorite = useFavoriteToggle();
  const maxDays = rangeLimitFor(item.key);
  const error = item.periodic ? rangeError(range, maxDays) : null;
  const needsCompany = item.key === "PROFITABILITY" && !companyId;
  const headingId = `report-${item.key}`;

  return (
    <Card className="grid h-full content-start gap-3 p-5" aria-labelledby={headingId}>
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-1">
          <h2 id={headingId} className="text-base font-semibold">
            {item.label}
          </h2>
          <p className="text-sm text-muted-foreground">{item.description}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-pressed={item.favorite}
          aria-label={
            item.favorite
              ? t("reports.center.unfavorite", { name: item.label })
              : t("reports.center.favorite", { name: item.label })
          }
          disabled={!item.available || favorite.isPending}
          onClick={() =>
            favorite.mutate(
              { reportKey: item.key, favorite: !item.favorite },
              {
                onError: (caught) =>
                  toast({
                    tone: "danger",
                    title: t("reports.center.favoriteFailed"),
                    description: errorMessage(caught),
                  }),
              },
            )
          }
        >
          <Star aria-hidden="true" className={item.favorite ? "fill-amber-500 text-amber-600" : undefined} />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {item.periodic ? (
          <StatusBadge tone="info">{t("reports.center.periodic", { days: maxDays })}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{t("reports.center.snapshot")}</StatusBadge>
        )}
        {!item.available ? <StatusBadge tone="warning">{t("reports.center.unavailable")}</StatusBadge> : null}
      </div>
      {item.available ? (
        <div className="mt-auto flex flex-wrap items-center gap-2">
          {page ? (
            <Button asChild>
              <Link to={page}>{t("reports.center.open")}</Link>
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">{t("reports.center.exportOnly")}</span>
          )}
          <div className="ml-auto">
            <ExportButtons
              reportKey={item.key}
              state={{ range, preset: null, companyId, stationIds: [], stationId: null, windowDays: 3 }}
              disabled={Boolean(error) || needsCompany}
            />
          </div>
          {error ? <p className="w-full text-xs text-destructive">{error}</p> : null}
          {needsCompany ? (
            <p className="w-full text-xs text-muted-foreground">{t("reports.center.companyNeeded")}</p>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("reports.center.unavailableHint")}</p>
      )}
    </Card>
  );
}
